import { assign, fromPromise, setup } from 'xstate';
import type { MonetaryValue } from '../graphql/query/getRefundOptions';
import { z } from 'zod';
import type { NavigateOptions } from '@tanstack/react-router';

import {
  getStorageJson,
  safeSessionStorage,
  setStorageJson,
} from '@repo/lib/storage';
import { isStepValid, validateStep } from './schema';
import type { AuthStatus, Steps, Workflow, WorkflowData } from './schemaTypes';

// Re-export types for backward compatibility
export type { WorkflowData, WorkflowItemData, ItemId } from './schemaTypes';
import { queryClient } from '../queryClient';
import { client } from '../graphql/client';
import { type Order, type SubmittedRequest } from '../graphql/query/getOrder';

import { getConfig } from './config';
import { getCartQueryFn, type GetCartResult } from '../graphql/query/getCart';
import { QUERIES, STORAGE_KEYS } from '../utils/constants';
import { clientLogger } from '../utils/logger';
import { getParams } from '../utils/searchParams';
import { queryOrder } from '../utils/queries';
import { injectRetailerTheme } from '../utils/theme';
import {
  logToTitan,
  type TitanEventType,
  workflowToTitanEvent,
} from '../utils/titan';
import type {
  RetailerSettings,
  Styles,
} from '../graphql/query/getRetailerSettings';
import { getRetailerFromUrl } from '../utils/getRetailerFromUrl';
import { pdxEvent } from '../utils/noflake';

export type WorkflowEvents =
  | { type: 'complete' }
  | { type: 'goTo'; stepId: Steps }
  | { type: 'logout' }
  | { type: 'next'; data: WorkflowData }
  | { type: 'prev' }
  | { type: 'updateAuthStatus'; status: AuthStatus }
  | { type: 'updateData'; data: WorkflowData }
  | { type: 'updateIsSaving'; isSaving: boolean }
  | {
      type: 'setContextFromLogin';
      data: {
        loginContext: {
          email?: string;
          orderNumber?: string;
          zipCode?: string;
        };
        order: Order;
        submittedRequests: SubmittedRequest[];
      };
    }
  | {
      type: 'setRefundAmount';
      refundAmount: MonetaryValue | null;
    }
  | { type: 'submit' }
  | { type: 'xstate.init' }
  | {
      type: 'xstate.done.actor.getConfig';
      actorId: 'getConfig';
      output: { config: Workflow; retailerSettings: RetailerSettings };
    }
  | {
      type: 'xstate.done.actor.getOrder';
      actorId: 'getOrder';
      output: {
        order: Order;
        submittedRequests: SubmittedRequest[];
      };
    }
  | {
      type: 'xstate.done.actor.getServerState';
      actorId: 'getServerState';
      output: WorkflowData;
    }
  | {
      type: 'xstate.error.actor.getConfig';
      actorId: 'getConfig';
      data: unknown;
    }
  | {
      type: 'xstate.error.actor.getOrder';
      actorId: 'getOrder';
      data: unknown;
    }
  | {
      type: 'xstate.error.actor.getServerState';
      actorId: 'getServerState';
      data: unknown;
    };

export type WorkflowContext = {
  authStatus: AuthStatus;
  config: Workflow | null;
  currentStepIndex: number;
  data: WorkflowData | null;
  // email: string | null;
  errors: Record<string, z.ZodError | null>;
  retailerSettings: RetailerSettings | null;
  isSaving: boolean;
  isLoading: {
    order: boolean;
  };
  loginContext: {
    email: string | null;
    orderNumber: string | null;
    zipCode: string | null;
  };
  order: Order | null;
  orderNumber: string | null;
  requestId: string | null;
  retailer: string | null;
  shid: string | null;
  submittedRequests: SubmittedRequest[] | null;
  token: string | null;
  // zipCode: string | null;
  error: Error | null;
  orderError: Error | null;
  redirectTo: NavigateOptions | null;
  refundAmount: MonetaryValue | null;
};

const isValidParam = (value: unknown): value is string =>
  typeof value === 'string' && value !== 'undefined' && value.length > 0;

export const workflowMachine = setup({
  types: {
    context: {} as WorkflowContext,
    events: {} as WorkflowEvents,
  },
  actions: {
    goToStep: assign({
      currentStepIndex: ({ context, event }) => {
        const { config } = context;
        if (!config || event.type !== 'goTo') return context.currentStepIndex;

        const stepIndex = config.steps.findIndex(
          (step) => step.id === event.stepId,
        );

        const newStepIndex =
          stepIndex >= 0 ? stepIndex : context.currentStepIndex;

        setStorageJson(
          safeSessionStorage,
          STORAGE_KEYS.CURRENT_STEP,
          newStepIndex,
        );

        return newStepIndex;
      },
    }),
    handleComplete: () => {
      safeSessionStorage.clear();
    },
    logger: ({ context, event }) => {
      const { config, currentStepIndex } = context;
      if (!Object.keys(workflowToTitanEvent).some((key) => key === event.type))
        return;

      const currentStepId = config?.steps?.[currentStepIndex]?.id;
      const titanEvent = workflowToTitanEvent[event.type] as TitanEventType;
      if (!titanEvent) return;

      pdxEvent({
        event_category: 'workflow',
        event_type: titanEvent,
        event_value: currentStepId,
      });

      try {
        const extraProperties = currentStepId
          ? [{ key: 'step', value: currentStepId }]
          : undefined;
        const workflowData = context.data
          ? {
              shipping_method:
                context.data.returnMethod && 'type' in context.data.returnMethod
                  ? context.data.returnMethod.type
                  : undefined,
              payment_method:
                context.data.refund && 'method' in context.data.refund
                  ? context.data.refund.method
                  : undefined,
            }
          : undefined;

        logToTitan({
          event: titanEvent,
          properties: {
            narvar_product_context: 'returns',
            extra_properties: extraProperties,
            order_id: context.order?.orderNumber || context.orderNumber,
            cart_id: context.requestId,
            ...workflowData,
          },
        });
      } catch (err) {
        clientLogger.logError(err);
        // eslint-disable-next-line eslint(no-console)
        console.error(`Logging error: ${err}`);
      }
    },
    moveToNextStep: assign({
      data: ({ context, event }) => {
        if (event.type !== 'next') return context.data;
        const newData = {
          ...context.data,
          ...event.data,
        };
        setStorageJson(safeSessionStorage, STORAGE_KEYS.DATA, newData);
        return newData as WorkflowData;
      },
      currentStepIndex: ({ context, event }) => {
        if (event.type !== 'next') return context.currentStepIndex;

        const { config, currentStepIndex } = context;
        if (!config) return currentStepIndex;

        // First validate with the new data
        const currentStep = config.steps[currentStepIndex];
        if (!currentStep) return currentStepIndex;

        const newData = {
          ...context.data,
          ...event.data,
        };

        const errors = validateStep(currentStep, newData);
        const isValid = isStepValid(errors, currentStep);

        if (!isValid) return currentStepIndex;

        // If valid, move to next step
        const newStepIndex =
          currentStepIndex < config.steps.length - 1
            ? currentStepIndex + 1
            : currentStepIndex;

        setStorageJson(
          safeSessionStorage,
          STORAGE_KEYS.CURRENT_STEP,
          newStepIndex,
        );
        return newStepIndex;
      },
    }),
    moveToPrevStep: assign({
      currentStepIndex: ({ context }) => {
        const { config, currentStepIndex } = context;
        if (!config) return context.currentStepIndex;

        const newStepIndex = currentStepIndex >= 1 ? currentStepIndex - 1 : 0;

        setStorageJson(
          safeSessionStorage,
          STORAGE_KEYS.CURRENT_STEP,
          newStepIndex,
        );

        return newStepIndex;
      },
    }),
    setAuthStatus: assign({
      authStatus: ({ context, event }) => {
        if (event.type !== 'updateAuthStatus') return context.authStatus;
        safeSessionStorage.setItem(STORAGE_KEYS.AUTH_STATUS, event.status);
        return event.status;
      },
    }),
    setConfig: assign({
      retailerSettings: ({ context, event }) => {
        if (event.type !== 'xstate.done.actor.getConfig') return null;

        const { retailer = '' } = context;
        const { retailerSettings } = event.output;
        const styles = retailerSettings.styles as Styles;

        if (styles) {
          injectRetailerTheme({
            options: {
              retailer,
            },
            styles,
          });
          setStorageJson(safeSessionStorage, STORAGE_KEYS.THEME, styles);
        }

        return event.output.retailerSettings;
      },
      config: ({ event }) => {
        if (event.type !== 'xstate.done.actor.getConfig') return null;
        return event.output.config;
      },
      currentStepIndex: () => {
        const storedCurrentStep = getStorageJson(
          safeSessionStorage,
          STORAGE_KEYS.CURRENT_STEP,
          z.number(),
        );
        return storedCurrentStep ?? 0;
      },
    }),
    setData: assign({
      data: ({ context, event }) => {
        const { data } = context;

        if (event.type !== 'updateData') return data;

        const newData = {
          ...data,
          ...event.data,
        };

        setStorageJson(safeSessionStorage, STORAGE_KEYS.DATA, newData);

        return newData as WorkflowData;
      },
    }),
    setContextFromLogin: assign({
      loginContext: ({ context, event }) => {
        if (event.type !== 'setContextFromLogin') return context.loginContext;

        return {
          email: event.data?.loginContext?.email ?? null,
          orderNumber: event.data?.loginContext?.orderNumber ?? null,
          zipCode: event.data?.loginContext?.zipCode ?? null,
        };
      },
      order: ({ event }) => {
        if (event.type !== 'setContextFromLogin') return null;

        return event.data.order;
      },
      submittedRequests: ({ event }) => {
        if (event.type !== 'setContextFromLogin') return null;

        return event.data.submittedRequests;
      },
    }),
    setOrder: assign({
      order: ({ event }) => {
        if (event.type !== 'xstate.done.actor.getOrder' || !event.output)
          return null;

        return event.output.order;
      },
      submittedRequests: ({ event }) => {
        if (event.type !== 'xstate.done.actor.getOrder' || !event.output)
          return null;

        return event.output.submittedRequests;
      },
    }),
    setIsSaving: assign({
      isSaving: ({ context, event }) => {
        if (event.type !== 'updateIsSaving') {
          return context.isSaving;
        }
        return event.isSaving;
      },
    }),
    setRefundAmount: assign({
      refundAmount: ({ context, event }) => {
        if (event.type !== 'setRefundAmount') {
          return context.refundAmount;
        }
        return event.refundAmount;
      },
    }),
    setContextFromUrl: assign({
      loginContext: ({ context }) => {
        const params = getParams();

        const orderNumber =
          params?.order ??
          params?.orderNumber ??
          context.loginContext.orderNumber;

        if (
          typeof orderNumber === 'string' &&
          context.orderNumber !== orderNumber
        ) {
          safeSessionStorage.clear();
          client.setHeaders({});
          safeSessionStorage.setItem(STORAGE_KEYS.ORDER_NUMBER, orderNumber);
        }

        return {
          email: params?.email ?? context.loginContext.email,
          orderNumber,
          zipCode: params?.zipCode ?? context.loginContext.zipCode,
        };
      },
      orderNumber: ({ context }) => {
        const params = getParams();
        return params?.order ?? params?.orderNumber ?? context.orderNumber;
      },
      retailer: ({ context }) => {
        const params = getParams();
        const retailer = params?.retailer ?? getRetailerFromUrl();
        const shid = params?.shid;

        if (retailer || shid) {
          clientLogger.associateErrorsWithRetailer({
            retailerMoniker: retailer,
            shid,
          });
        }

        return retailer ?? context.retailer;
      },
      shid: ({ context }) => {
        const params = getParams();
        return params?.shid ?? context.shid;
      },
      token: ({ context }) => {
        const params = getParams();
        const token = params?.token;
        if (token) {
          client.setHeader('X-Authentication', token);
          return token;
        }
        return context.token;
      },
      requestId: ({ context }) => {
        const params = getParams();
        return params?.request ?? context.requestId;
      },
    }),
    processServerState: assign({
      authStatus: 'authenticated',
      currentStepIndex: ({ context, event }) => {
        const { config } = context;

        if (event.type !== 'xstate.done.actor.getServerState' || !config) {
          return 0;
        }

        for (let i = 0; i < config.steps.length; i++) {
          const step = config.steps[i];
          if (!step) {
            return Math.max(i - 1, 0);
          }
          const errors = validateStep(step, event.output);
          const isValid = isStepValid(errors, step);

          if (!isValid) {
            return i;
          }
        }

        return config.steps.length - 1;
      },
      data: ({ context, event }) => {
        if (event.type !== 'xstate.done.actor.getServerState') {
          return context.data;
        }
        return event.output;
      },
    }),
    setRedirectToClaimDetails: assign({
      redirectTo: ({ context }) => {
        if (
          !context.requestId ||
          !context.submittedRequests ||
          !context.orderNumber ||
          !context.retailer
        ) {
          return null;
        }
        const match = context.submittedRequests.find(
          (req) => req.id === context.requestId,
        );
        if (match) {
          return {
            to: '/$retailer/claim-details' as const,
            params: { retailer: context.retailer },
            search: {
              order: context.orderNumber,
              claimId: context.requestId,
            },
          };
        }
        return null;
      },
    }),
    setRedirectToClaimDetailsOnError: assign({
      redirectTo: ({ context }) => {
        const path = '/claim-details';

        pdxEvent({
          event_category: 'redirect',
          event_type: 'redirect',
          event_value: `invalid token redirecting to ${path}`,
        });

        const val =
          !context.requestId || !context.orderNumber || !context.retailer
            ? null
            : {
                to: `/$retailer${path}` as const,
                params: { retailer: context.retailer },
                search: {
                  order: context.orderNumber,
                  claimId: context.requestId,
                },
              };
        return val;
      },
    }),
    handleLogout: assign(({ context }) => {
      safeSessionStorage.clear();
      client.setHeaders({});

      return {
        retailer: context.retailer,
        shid: context.shid,
        config: null,
        retailerSettings: null,
        authStatus: 'unauthenticated' as const,
        currentStepIndex: 0,
        data: null,
        errors: {},
        order: null,
        submittedRequests: null,
        loginContext: {
          email: null,
          orderNumber: null,
          zipCode: null,
        },
        orderNumber: null,
        requestId: null,
        token: null,
        redirectTo: {
          to: '/$retailer' as const,
          params: { retailer: context.retailer },
          reloadDocument: true,
          search: true,
        } as NavigateOptions,
        refundAmount: null,
        error: null,
        orderError: null,
        isSaving: false,
        isLoading: {
          order: false,
        },
      };
    }),
  },
  actors: {
    fetchServerState: fromPromise(
      async ({
        input,
      }: {
        input: {
          config: Workflow | null;
          orderNumber: string | null;
          requestId: string | null;
          token: string | null;
        };
      }) => {
        const { config, orderNumber, requestId, token } = input;
        if (!orderNumber || !requestId || !token) return;

        const { cart } = await queryClient.fetchQuery<GetCartResult>({
          queryKey: [QUERIES.GET_CART, { orderNumber, token, requestId }],
          queryFn: () => getCartQueryFn(requestId),
        });

        if (!cart || cart.items.length === 0)
          return {
            itemSelection: [],
          };

        const isReasonTaskItemScoped = config?.steps.some((step) =>
          step.tasks.some(
            (task) => task.scope === 'item' && task.id === 'reason',
          ),
        );

        const isResolutionTaskItemScoped = config?.steps.some((step) =>
          step.tasks.some(
            (task) => task.scope === 'item' && task.id === 'resolution',
          ),
        );

        const requiresItemsObject =
          isReasonTaskItemScoped || isResolutionTaskItemScoped;

        return cart.items.reduce<WorkflowData>((acc, item) => {
          if (!acc.items) acc.items = {};
          acc.items[item.id] = {
            itemSelection: { id: item.id, quantity: item.quantity },
          };

          const reasonValue = item.reason?.id;
          const reasonId =
            typeof reasonValue === 'string' ? reasonValue : undefined;
          const resolutionValue = item.resolution;
          const resolutionId =
            typeof resolutionValue === 'string' ? resolutionValue : undefined;

          if (requiresItemsObject) {
            if (acc.items) {
              if (!acc.items[item.id]) {
                acc.items[item.id] = {};
              }

              const itemData = acc.items[item.id];

              if (isReasonTaskItemScoped && itemData) {
                itemData.reason = { reasonId };
              }
              if (isResolutionTaskItemScoped && itemData) {
                itemData.resolution = { resolutionId };
              }
            }
          } else if (!requiresItemsObject) {
            if (
              !isReasonTaskItemScoped &&
              reasonId !== undefined &&
              !acc.reason
            ) {
              acc.reason = { reasonId };
            }
            if (
              !isResolutionTaskItemScoped &&
              resolutionId !== undefined &&
              !acc.resolution
            ) {
              acc.resolution = { resolutionId };
            }
          }

          return acc;
        }, {});
      },
    ),
    fetchConfig: fromPromise(
      async ({
        input,
      }: {
        input: {
          requestId: string | null;
          retailer: string | null;
          token: string | null;
        };
      }) => {
        const styles = getStorageJson(
          safeSessionStorage,
          STORAGE_KEYS.THEME,
          // TODO: use a zod schema instead of `as`
          z.unknown(),
        ) as Styles;
        if (styles) {
          injectRetailerTheme({
            styles,
            options: { retailer: input.retailer },
          });
        }

        const config = await getConfig();
        return config;
      },
    ),
    fetchOrder: fromPromise(
      async ({
        input: { email, orderNumber, zipCode, requestId },
      }: {
        input: {
          email: string | null;
          orderNumber: string | null;
          retailer: string | null;
          zipCode: string | null;
          requestId: string | null;
        };
      }) => {
        if (!orderNumber) {
          return null;
        }

        const data = await queryOrder({
          email,
          orderNumber,
          zipCode,
          requestId,
        });

        if ('error' in data) {
          throw new Error(JSON.stringify(data.error));
        }

        safeSessionStorage.setItem(
          STORAGE_KEYS.AUTH_TOKEN,
          data.order.authorization.token,
        );
        return data;
      },
    ),
  },
  guards: {
    isAuthenticated: ({ context }) => {
      return context.authStatus === 'authenticated';
    },
    isOrderlessAuthenticated: ({ context }) => {
      // For orderless flows, check if orderless is enabled instead of requiring authentication
      return context.retailerSettings?.features?.isOrderless === true;
    },
    isRehydrateFromServer: ({ context }) =>
      Boolean(context.requestId && context.orderNumber && context.token),
    hasConfig: ({ context }) => {
      return context.config !== null;
    },
    hasOrder: ({ context }) => {
      return context.order !== null;
    },
    hasOrderNumber: ({ context }) => {
      return context.orderNumber !== null;
    },
    canRedirectToClaimDetails: ({ context }) =>
      Boolean(
        isValidParam(context.requestId) &&
          isValidParam(context.orderNumber) &&
          isValidParam(context.retailer),
      ),
  },
}).createMachine({
  id: 'workflow',
  initial: 'checkUrl',
  entry: 'logger',
  context: {
    authStatus: safeSessionStorage.getItem(
      STORAGE_KEYS.AUTH_STATUS,
      // TODO: this `as` should be AuthStatus | null but that causes a type error
    ) as AuthStatus, // ToDo: Needs real auth flow.
    retailerSettings: null,
    config: null,
    currentStepIndex: 0,
    data: getStorageJson(
      safeSessionStorage,
      STORAGE_KEYS.DATA,
      // TODO: use a zod schema instead of `as`
      z.unknown(),
    ) as WorkflowData | null,
    errors: {},
    isSaving: false,
    isLoading: {
      order: false,
    },
    loginContext: {
      email: null,
      orderNumber: null,
      zipCode: null,
    },
    order: null,
    orderNumber: safeSessionStorage.getItem(STORAGE_KEYS.ORDER_NUMBER),
    requestId: null,
    retailer: null,
    shid: null,
    submittedRequests: null,
    token: safeSessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN),
    error: null,
    orderError: null,
    redirectTo: null,
    refundAmount: null,
  },
  states: {
    checkUrl: {
      always: {
        actions: 'setContextFromUrl',
        target: 'loadConfig',
      },
    },
    loadConfig: {
      invoke: {
        id: 'getConfig',
        src: 'fetchConfig',
        input: ({ context: { requestId, retailer, token } }) => ({
          requestId,
          retailer,
          token,
        }),
        onDone: {
          actions: 'setConfig',
          target: 'loadOrder',
        },
        onError: {
          target: 'error',
          actions: assign({
            error: (_, event: unknown) => {
              if (
                event &&
                typeof event === 'object' &&
                'data' in event &&
                event.data instanceof Error
              ) {
                return event.data;
              }
              return new Error('Config loading failed');
            },
          }),
        },
      },
    },
    loadOrder: {
      entry: assign({
        isLoading: ({ context }) => ({ ...context.isLoading, order: true }),
      }),
      exit: assign({
        isLoading: ({ context }) => ({ ...context.isLoading, order: false }),
      }),
      invoke: {
        id: 'getOrder',
        src: 'fetchOrder',
        input: ({
          context: { orderNumber, loginContext, retailer, requestId },
        }) => {
          const { email, zipCode } = loginContext;
          return {
            orderNumber,
            email,
            retailer,
            zipCode,
            requestId,
          };
        },
        onDone: [
          {
            actions: ['setOrder', 'setRedirectToClaimDetails'],
            guard: 'hasOrderNumber',
            target: 'checkAuth',
          },
          {
            actions: 'setRedirectToClaimDetails',
            target: 'checkAuth',
          },
        ],
        onError: [
          {
            guard: 'canRedirectToClaimDetails',
            actions: [
              assign({
                orderError: (_, event: unknown) => {
                  if (
                    event &&
                    typeof event === 'object' &&
                    'type' in event &&
                    event.type === 'xstate.error.actor.getOrder' &&
                    'data' in event
                  ) {
                    return event.data instanceof Error
                      ? event.data
                      : new Error(String(event.data));
                  }
                  return new Error('Unknown order error');
                },
                authStatus: () => {
                  safeSessionStorage.clear();
                  return 'unauthenticated';
                },
              }),
              'setRedirectToClaimDetailsOnError',
            ],
            target: 'ready',
          },
          {
            actions: [
              assign({
                orderError: (_, event: unknown) => {
                  if (
                    event &&
                    typeof event === 'object' &&
                    'type' in event &&
                    event.type === 'xstate.error.actor.getOrder' &&
                    'data' in event
                  ) {
                    return event.data instanceof Error
                      ? event.data
                      : new Error(String(event.data));
                  }
                  return new Error('Unknown order error');
                },
                authStatus: () => {
                  safeSessionStorage.clear();
                  return 'unauthenticated';
                },
              }),
            ],
            target: 'notAuthenticated',
          },
        ],
      },
    },
    checkAuth: {
      always: [
        {
          guard: 'isRehydrateFromServer',
          target: 'rehydrateFromServer',
        },
        {
          guard: 'isAuthenticated',
          target: 'ready',
        },
        {
          guard: 'isOrderlessAuthenticated',
          actions: assign({
            authStatus: 'authenticated',
          }),
          target: 'ready',
        },
        {
          target: 'notAuthenticated',
        },
      ],
    },
    notAuthenticated: {
      on: {
        updateAuthStatus: {
          target: 'checkAuth',
          actions: 'setAuthStatus',
        },
      },
    },
    rehydrateFromServer: {
      invoke: {
        id: 'getServerState',
        src: 'fetchServerState',
        input: ({ context: { config, orderNumber, requestId, token } }) => ({
          config,
          orderNumber,
          requestId,
          token,
        }),
        onDone: {
          actions: 'processServerState',
          target: 'ready',
        },
        onError: {
          target: 'error',
          actions: assign({
            error: (_, event: unknown) => {
              if (
                event &&
                typeof event === 'object' &&
                'type' in event &&
                event.type === 'xstate.error.actor.getServerState' &&
                'data' in event
              ) {
                return event.data instanceof Error
                  ? event.data
                  : new Error(String(event.data));
              }
              return new Error('Unknown server state error');
            },
          }),
        },
      },
    },
    error: {
      entry: 'logger',
      type: 'final',
    },
    logout: {
      entry: 'handleLogout',
      always: {
        target: 'notAuthenticated',
      },
    },
    ready: {
      initial: 'active',
      entry: 'logger',
      states: {
        active: {
          on: {
            complete: {
              actions: ['handleComplete', 'logger'],
              target: 'complete',
            },
            goTo: {
              actions: ['goToStep', 'logger'],
            },
            logout: {
              target: '#workflow.logout',
            },
            next: {
              actions: ['moveToNextStep', 'logger'],
            },
            prev: {
              actions: ['moveToPrevStep', 'logger'],
            },
            setContextFromLogin: { actions: 'setContextFromLogin' },
            updateData: {
              actions: 'setData',
              target: 'active',
            },
            updateIsSaving: {
              actions: 'setIsSaving',
              target: 'active',
            },
            setRefundAmount: { actions: 'setRefundAmount' },
          },
        },
        complete: {
          type: 'final',
        },
      },
    },
  },
});
